/**
 * Copy text to the clipboard, reporting honestly whether it worked.
 *
 * Why copying used to fail intermittently, and why the failure was invisible:
 *
 *  1. `document.execCommand('copy')` returns **false** on failure instead of throwing.
 *     Every call site wrapped it in try/catch and showed "copied" in the try block, so
 *     a failed copy produced a success message and an empty clipboard.
 *
 *  2. Browsers only honour a clipboard write during a **user-gesture task**. Awaiting
 *     anything first (a fetch, a confirm dialog, even an already-resolved promise)
 *     ends that task, and the write is then refused. This is the usual reason a copy
 *     works one moment and not the next.
 *
 *  3. `navigator.clipboard` is unavailable on plain HTTP (`isSecureContext` false) and
 *     rejects when the document is not focused — clicking a button in a background
 *     window, or with devtools focused, is enough.
 *
 * The synchronous `execCommand` path is therefore attempted FIRST: it still counts as
 * running inside the click handler even when the caller has already awaited something.
 * `navigator.clipboard` is the fallback, since it is the one that reports a real error.
 *
 * Returns true only when the text actually reached the clipboard.
 */
export async function copyTextToClipboard(text) {
  if (typeof text !== "string" || text.length === 0) return false;

  if (legacyCopy(text)) return true;

  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Not focused, permission denied, or the gesture has already expired.
    }
  }

  return false;
}

/**
 * Synchronous clipboard write via a temporary textarea.
 *
 * Kept synchronous on purpose — an await anywhere before `execCommand` would end the
 * user-gesture task and the browser would refuse the write.
 */
function legacyCopy(text) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  // Off-screen but still selectable: display:none and visibility:hidden both make the
  // selection empty, so execCommand would copy nothing and still report success.
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "-9999px";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);

  const previouslyFocused = document.activeElement;

  let copied = false;
  try {
    textarea.focus();
    textarea.select();
    // iOS ignores select() on a readonly field unless the range is set explicitly.
    textarea.setSelectionRange(0, textarea.value.length);
    copied = document.execCommand("copy") === true;
  } catch {
    copied = false;
  } finally {
    document.body.removeChild(textarea);
    // Give focus back, otherwise copying steals the caret from whatever the user was
    // typing in.
    if (previouslyFocused && typeof previouslyFocused.focus === "function") {
      previouslyFocused.focus();
    }
  }

  return copied;
}
